// Client entry point: connects to the server, owns the canvas + rAF loop,
// and dispatches incoming ServerMessages to the relevant UI module. Stages
// 2+ add real dungeon/hero/minion rendering here; for stage 1 the loop just
// keeps the canvas sized and painted while the lobby panel does the work.

import { Net } from "./net";
import { initLobby, updateLobby } from "./lobby";
import { initArchitectInput, initHeroInput, initPointerInput } from "./input";
import { type AbilityPreview, type Camera, computeCamera, type HeroSpellPreview, renderDungeon, renderHud, type SpellCastHint } from "./render";
import { playWarningBeep } from "./audio";
import { CONFIG } from "../shared/config";
import { spellCastHintLines } from "../shared/spellInfo";
import type { PieceType } from "../shared/pieces";
import type { DungeonSnapshot, GroundEffectSnapshot, HeroSnapshot, LevelUpStat, MinionSnapshot, MinionUpgradeLevels, Role, SpellSlot } from "../shared/types";

// Matches src/input.ts's SPELL_KEY_SLOTS (Q/E/R) — used only for the "press
// [key] again to cancel" wording in the armed-spell cursor hint.
const SPELL_KEY_LABELS: Record<SpellSlot, string> = { 1: "Q", 2: "E", 3: "R" };

const canvas = document.getElementById("game-canvas") as HTMLCanvasElement;
const ctx = canvas.getContext("2d");
if (!ctx) throw new Error("2D context not available");

const connectionBanner = document.getElementById("connection-banner") as HTMLDivElement;
const gameOverOverlay = document.getElementById("game-over-overlay") as HTMLDivElement;
const gameOverTitle = document.getElementById("game-over-title") as HTMLHeadingElement;
document.getElementById("btn-reload")?.addEventListener("click", () => location.reload());

const levelUpOverlay = document.getElementById("levelup-overlay") as HTMLDivElement;
const godModeBtn = document.getElementById("btn-godmode") as HTMLButtonElement;
const returnLobbyBtn = document.getElementById("btn-return-lobby") as HTMLButtonElement;

const debugPanel = document.getElementById("debug-panel") as HTMLDivElement;
const debugGoldBtn = document.getElementById("btn-debug-gold") as HTMLButtonElement;
const debugPiecePicker = document.getElementById("debug-piece-picker") as HTMLDivElement;
const debugRefreshCooldownsBtn = document.getElementById("btn-debug-refresh-cooldowns") as HTMLButtonElement;
const debugGrantXpBtn = document.getElementById("btn-debug-grant-xp") as HTMLButtonElement;

let myRole: Role | null = null;
let latestDungeon: DungeonSnapshot | null = null;
let latestHeroes: HeroSnapshot[] = [];
let latestMinions: MinionSnapshot[] = [];
let latestGroundEffects: GroundEffectSnapshot[] = [];
let latestFlameBarrierRow = 0;
let latestArchitectGodMode = false;
let latestArchitectGold = 0;
let latestMinionUpgradeLevels: MinionUpgradeLevels = { minionSpeed: 0, minionHealth: 0, minionDamage: 0, minionCount: 0 };
let currentCamera: Camera | null = null; // recomputed every render frame — read by pointer input for screen->world conversion

// When the current match started (client-local, set on the first "state"
// message of a fresh match) — drives the "show attack range for the first
// few seconds" HUD aid (see CONFIG.heroAttackRangeShowSeconds).
let matchStartTime: number | null = null;

// Tracks the last whole-second boundary the low-timer beep fired on, so the
// warning ticks roughly once per second while under the threshold instead of
// once per incoming "state" message (see CONFIG.checkpointTimerLowSeconds).
let lastLowTimerBeepSecond: number | null = null;
function maybePlayLowTimerBeep(remainingSeconds: number): void {
  if (myRole !== "architect" || remainingSeconds > CONFIG.checkpointTimerLowSeconds) {
    lastLowTimerBeepSecond = null;
    return;
  }
  const secondFloor = Math.floor(remainingSeconds);
  if (secondFloor === lastLowTimerBeepSecond) return;
  lastLowTimerBeepSecond = secondFloor;
  playWarningBeep();
}

// God Mode debug tools (not part of the design doc — see CLAUDE.md's "Debug
// tools"): visible only while the local player's own God Mode is active,
// and split by role (architect gets gold/piece controls, heroes get
// cooldown/xp controls) since each role's toggle only unlocks its own set.
function updateGodModeButton(): void {
  if (!myRole) {
    godModeBtn.classList.add("hidden");
    debugPanel.classList.add("hidden");
    return;
  }
  const isArchitect = myRole === "architect";
  const active = isArchitect ? latestArchitectGodMode : (latestHeroes.find((h) => h.role === myRole)?.godMode ?? false);
  godModeBtn.classList.remove("hidden");
  godModeBtn.classList.toggle("active", active);
  godModeBtn.textContent = `God Mode: ${active ? "ON" : "OFF"}`;

  debugPanel.classList.toggle("hidden", !active);
  debugGoldBtn.classList.toggle("hidden", !isArchitect);
  debugPiecePicker.classList.toggle("hidden", !isArchitect);
  debugRefreshCooldownsBtn.classList.toggle("hidden", isArchitect);
  debugGrantXpBtn.classList.toggle("hidden", isArchitect);
}

function resizeCanvas(): void {
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
}
window.addEventListener("resize", resizeCanvas);
resizeCanvas();

const net = new Net();

net.onStatusChange((connected) => {
  connectionBanner.classList.toggle("hidden", connected);
});

net.onMessage((message) => {
  if (message.type === "lobby") {
    myRole = message.youAreRole;
    updateLobby(message);
    // The room resets itself back to "lobby" a few seconds after a match
    // ends (see server/room.ts) for an instant rematch — clear the previous
    // match's overlay/snapshot so it doesn't linger over the new one. If the
    // same roles are still claimed this can jump straight from "gameover" to
    // "playing" with no observable "lobby" tick in between, so the "state"
    // branch below clears the overlay too, defensively.
    if (message.phase !== "gameover") {
      gameOverOverlay.classList.add("hidden");
    }
    if (message.phase === "lobby") {
      latestDungeon = null;
      latestHeroes = [];
      latestMinions = [];
      levelUpOverlay.classList.add("hidden");
      matchStartTime = null;
      lastLowTimerBeepSecond = null;
    }
    // Available to anyone once a match exists — even a spectator with no
    // role, since if everyone else disconnected this is the only way back
    // to a usable lobby (see server/room.ts's forceReturnToLobby).
    returnLobbyBtn.classList.toggle("hidden", message.phase === "lobby");
    updateGodModeButton();
  } else if (message.type === "state") {
    if (latestDungeon === null) matchStartTime = Date.now(); // first "state" tick of a fresh match
    maybePlayLowTimerBeep(message.dungeon.checkpointTimerRemaining);
    latestDungeon = message.dungeon;
    latestHeroes = message.heroes;
    latestMinions = message.minions;
    latestGroundEffects = message.groundEffects;
    latestFlameBarrierRow = message.flameBarrierRow;
    latestArchitectGodMode = message.architectGodMode;
    latestArchitectGold = message.architectGold;
    latestMinionUpgradeLevels = message.minionUpgradeLevels;
    gameOverOverlay.classList.add("hidden");
    returnLobbyBtn.classList.remove("hidden");

    const myHero = myRole ? message.heroes.find((h) => h.role === myRole) : undefined;
    levelUpOverlay.classList.toggle("hidden", !myHero || myHero.pendingLevelUps <= 0);
    updateGodModeButton();
  } else if (message.type === "gameOver") {
    gameOverTitle.textContent = message.winner === "architect" ? "The architect wins!" : "The heroes win!";
    gameOverOverlay.classList.remove("hidden");
  }
});

initLobby(
  (role) => net.send({ type: "claimRole", role }),
  (heroClass) => net.send({ type: "selectClass", class: heroClass }),
  () => net.send({ type: "startGame" }),
);
initArchitectInput(net, () => myRole);
initHeroInput(net, () => myRole);
const pointerInput = initPointerInput(net, canvas, () => myRole, () => currentCamera);

godModeBtn.addEventListener("click", () => net.send({ type: "toggleGodMode" }));
returnLobbyBtn.addEventListener("click", () => net.send({ type: "returnToLobby" }));

debugGoldBtn.addEventListener("click", () => net.send({ type: "debugGrantGold" }));
debugRefreshCooldownsBtn.addEventListener("click", () => net.send({ type: "debugRefreshCooldowns" }));
debugGrantXpBtn.addEventListener("click", () => net.send({ type: "debugGrantLevelXp" }));
document.querySelectorAll<HTMLButtonElement>(".debug-piece-btn").forEach((btn) => {
  btn.addEventListener("click", () => {
    const pieceType = btn.dataset.piece as PieceType;
    net.send({ type: "debugSetNextPiece", pieceType });
  });
});

document.querySelectorAll<HTMLButtonElement>(".levelup-btn").forEach((btn) => {
  btn.addEventListener("click", () => {
    const stat = btn.dataset.stat as LevelUpStat;
    net.send({ type: "levelUpChoice", stat });
    // Optimistically hide — if more level-ups are still pending, the next
    // "state" message (a tick or two away) will show it again.
    levelUpOverlay.classList.add("hidden");
  });
});

let lastLoggedError: unknown;
function loop(): void {
  try {
    if (latestDungeon) {
      currentCamera = computeCamera(canvas, latestDungeon, latestHeroes, myRole);
      const armedAbility = pointerInput.getArmedAbility();
      const mouseWorld = pointerInput.getMouseWorld();
      const abilityPreview: AbilityPreview | null = armedAbility && mouseWorld ? { ability: armedAbility, point: mouseWorld } : null;

      const armedSpellSlot = pointerInput.getArmedSpellSlot();
      const myHeroForPreview = myRole ? latestHeroes.find((h) => h.role === myRole) : undefined;
      const heroSpellPreview: HeroSpellPreview | null =
        armedSpellSlot !== null && myHeroForPreview && mouseWorld
          ? { heroClass: myHeroForPreview.class, slot: armedSpellSlot, caster: { x: myHeroForPreview.x, y: myHeroForPreview.y }, aim: mouseWorld }
          : null;
      // Casting a spell needs a follow-up click, which players kept missing
      // — this instruction follows the cursor the moment a spell is armed.
      const mouseScreen = pointerInput.getMouseScreen();
      const spellCastHint: SpellCastHint | null =
        armedSpellSlot !== null && myHeroForPreview && mouseScreen
          ? { screen: mouseScreen, lines: spellCastHintLines(myHeroForPreview.class, armedSpellSlot, SPELL_KEY_LABELS[armedSpellSlot]) }
          : null;

      const showAttackRange = matchStartTime !== null && (Date.now() - matchStartTime) / 1000 < CONFIG.heroAttackRangeShowSeconds;
      renderDungeon(
        ctx!,
        canvas,
        latestDungeon,
        latestHeroes,
        latestMinions,
        latestGroundEffects,
        latestFlameBarrierRow,
        myRole,
        abilityPreview,
        heroSpellPreview,
        showAttackRange,
        spellCastHint,
      );
      renderHud(ctx!, latestDungeon, latestHeroes, latestArchitectGodMode, latestArchitectGold, latestMinionUpgradeLevels, myRole, armedAbility);
    } else {
      currentCamera = null;
      ctx!.fillStyle = "#0b0b10";
      ctx!.fillRect(0, 0, canvas.width, canvas.height);
      if (myRole) {
        ctx!.fillStyle = "#9a9ab0";
        ctx!.font = "16px system-ui, sans-serif";
        ctx!.fillText(`You are: ${myRole}`, 12, canvas.height - 16);
      }
    }
  } catch (err) {
    if (err !== lastLoggedError) {
      lastLoggedError = err;
      console.error("Render loop error:", err);
    }
  }
  requestAnimationFrame(loop);
}
requestAnimationFrame(loop);
